import { useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { usePatchState } from '@/shared/hooks';
import { capturePublicApi } from '../api/captureApi';
import type { InviteResolution } from '../api/captureApi';
import {
  LEGACY_QUESTION_SET,
  MAX_SUPPORTING_DETAILS,
  MAX_PEOPLE,
  PRIMARY_THEME_PROMPT,
  SUPPORTING_DETAIL_PROMPT,
  REASON_PROMPT,
  PEOPLE_PROMPT,
  OWN_WORDS_PROMPT,
  OWN_WORDS_SUBTITLE,
  OWN_WORDS_PLACEHOLDER,
  OWN_WORDS_PRIVACY_NOTE,
  OWN_WORDS_MAX_LENGTH,
  combinePeople,
  normalizePeopleWriteIns,
} from '../captureQuestions';
import { useFunnelTracker } from '../hooks/useFunnelTracker';
import { OptionBtn } from './CapturePagePrimitives';
import { CaptureShareScreen } from './CaptureShareScreen';
import { CaptureExpectationScreen } from './CaptureExpectationScreen';
import styles from '../pages/ReviewCapturePage.module.css';

type Step = 'q1' | 'q2' | 'reason' | 'people' | 'ownWords' | 'expectation' | 'share';

interface FlowState {
  step: Step;
  primaryTheme: string | null;
  supportingDetails: string[];
  reasonForVisit: string | null;
  people: string[];
  // Free-typed staff names ("one more appears once you fill one"), merged with
  // the tapped chips at submit. Always holds a trailing '' while there's room.
  peopleWriteIns: string[];
  // The patient's own words, in their own voice — see buildAssistUserMessage's
  // PATIENT'S OWN WORDS section on the backend for how this is prioritized.
  optionalDetail: string;
  draft: string;
  draftLoading: boolean;
}

function toggleCapped<T>(list: T[], value: T, max: number): T[] {
  if (list.includes(value)) return list.filter((v) => v !== value);
  if (list.length >= max) return list;
  return [...list, value];
}

export function CaptureHappyPathFlow({
  resolution,
  shortCode,
  rating,
  onBackToIntro,
}: {
  resolution: InviteResolution;
  shortCode: string;
  rating: number | null;
  onBackToIntro: () => void;
}) {
  const [state, set] = usePatchState<FlowState>({
    step: 'people',
    primaryTheme: null,
    supportingDetails: [],
    reasonForVisit: null,
    people: [],
    peopleWriteIns: [''],
    optionalDetail: '',
    draft: '',
    draftLoading: false,
  });

  const trackStep = useFunnelTracker(shortCode);

  const staffPeople = resolution.people;

  // Vertical/specialty-aware question sets from the server; fall back to the
  // legacy dental set if an older backend didn't send them.
  const questions = resolution.questions ?? LEGACY_QUESTION_SET;
  // Empty reasons = the reason step is disabled (practice privacy toggle or
  // sensitive-specialty tier) — skip that screen entirely.
  const hasReasonStep = questions.reasons.length > 0;

  // Ordered question steps. Rendered as step dots rather than a percentage
  // bar — a mostly-empty bar right after the star tap read as "long form
  // ahead" in the pilot. Order mirrors how patients recall a visit: who took
  // care of them, why they were there, then what stood out.
  const FLOW_STEPS: Step[] = hasReasonStep
    ? ['people', 'reason', 'q1', 'q2', 'ownWords']
    : ['people', 'q1', 'q2', 'ownWords'];
  const stepIndex = FLOW_STEPS.indexOf(state.step);

  const stepDots =
    stepIndex >= 0 ? (
      <div className={styles.dots} aria-hidden>
        {FLOW_STEPS.map((s, i) => (
          <span
            key={s}
            className={
              i < stepIndex ? styles.dotDone : i === stepIndex ? styles.dotActive : styles.dot
            }
          />
        ))}
      </div>
    ) : null;

  // Render-view funnel events, one per screen. Completion events fire on the
  // advancing tap; the view/complete pair is what separates "never saw the
  // screen" from "saw it and bailed", and their gap is the dwell time.
  useEffect(() => {
    const VIEW_EVENTS: Partial<Record<Step, Parameters<typeof trackStep>[0]>> = {
      people: 'view_people',
      reason: 'view_reason',
      q1: 'view_standout',
      q2: 'view_detail',
      ownWords: 'view_own_words',
      expectation: 'view_expectation',
    };
    const viewEvent = VIEW_EVENTS[state.step];
    if (viewEvent) trackStep(viewEvent);
  }, [state.step, trackStep]);

  // Single-select screens auto-advance on tap (after a beat so the chip shows
  // selected) — the star screen taught "tap = advance", and holding the wizard
  // to the same rule removes the hunt for a Next button. Multi-select screens
  // (people, details) keep an explicit Next.
  const AUTO_ADVANCE_MS = 250;

  function selectPrimaryTheme(theme: string) {
    set({ primaryTheme: theme, supportingDetails: [] });
    window.setTimeout(() => {
      trackStep('standout');
      set({ step: 'q2' });
    }, AUTO_ADVANCE_MS);
  }

  function toggleSupportingDetail(detail: string) {
    set({
      supportingDetails: toggleCapped(state.supportingDetails, detail, MAX_SUPPORTING_DETAILS),
    });
  }

  function selectReason(reason: string) {
    set({ reasonForVisit: reason });
    window.setTimeout(() => {
      trackStep('reason');
      set({ step: 'q1' });
    }, AUTO_ADVANCE_MS);
  }

  // Chips and write-ins share the MAX_PEOPLE budget.
  const typedCount = state.peopleWriteIns.filter((w) => w.trim() !== '').length;
  const selectedPeopleCount = state.people.length + typedCount;

  function togglePerson(name: string) {
    const people = toggleCapped(state.people, name, MAX_PEOPLE - typedCount);
    set({ people, peopleWriteIns: normalizePeopleWriteIns(state.peopleWriteIns, people.length) });
  }

  function setWriteIn(index: number, value: string) {
    const next = [...state.peopleWriteIns];
    next[index] = value;
    set({ peopleWriteIns: normalizePeopleWriteIns(next, state.people.length) });
  }

  const goToAfterPeople = () => {
    trackStep('people');
    set({ step: hasReasonStep ? 'reason' : 'q1' });
  };

  // Go straight to the expectation screen while the draft assembles in the
  // background — it sets up the Google hand-off before the patient ever sees
  // a button that could read as "done". They land on 'share' only once they've
  // tapped through it (see the 'expectation' step below).
  async function assembleDraft() {
    trackStep('own_words');
    set({ step: 'expectation', draftLoading: true, draft: '' });

    if (!state.primaryTheme || rating === null) {
      set({ draftLoading: false });
      return;
    }

    try {
      const res = await capturePublicApi.assembleAssist(shortCode, {
        rating,
        primaryTheme: state.primaryTheme,
        supportingDetails: state.supportingDetails,
        reasonForVisit: state.reasonForVisit ?? undefined,
        people: combinePeople(state.people, state.peopleWriteIns),
        optionalDetail: state.optionalDetail.trim() || undefined,
      });
      const draft = res.data?.draft ?? '';
      // 'draft' means the patient actually has something to post. A failed or
      // empty assemble leaves them staring at a blank box, which is a real
      // funnel loss and should read as one.
      if (draft) trackStep('draft');
      set({ draft, draftLoading: false });
    } catch {
      set({ draft: '', draftLoading: false });
    }
  }

  const supportingDetailOptions = state.primaryTheme
    ? (questions.detailsByTheme[state.primaryTheme] ?? [])
    : [];

  // Plain-English reminders of what the patient already told us — shown back
  // to them only if AI generation comes back empty, so writing their own
  // review from scratch never means re-remembering or re-entering anything
  // (see CaptureShareScreen's empty-draft state).
  const primaryThemeLabel = questions.themes.find((t) => t.value === state.primaryTheme)?.label;
  const reasonLabel = questions.reasons.find((r) => r.value === state.reasonForVisit)?.label;
  const patientInputs: string[] = [
    ...combinePeople(state.people, state.peopleWriteIns).map((name) => `${name} took care of you`),
    ...(primaryThemeLabel ? [primaryThemeLabel] : []),
    ...state.supportingDetails
      .map((v) => supportingDetailOptions.find((d) => d.value === v)?.label)
      .filter((label): label is string => !!label),
    ...(reasonLabel ? [reasonLabel] : []),
  ];

  return (
    <>
      {state.step === 'q1' && (
        <div className={styles.card}>
          {stepDots}
          <div className={styles.question}>{PRIMARY_THEME_PROMPT}</div>
          <div className={styles.options}>
            {questions.themes.map((theme) => (
              <OptionBtn
                key={theme.value}
                label={theme.label}
                sublabel={theme.sublabel}
                selected={state.primaryTheme === theme.value}
                onClick={() => selectPrimaryTheme(theme.value)}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => set({ step: hasReasonStep ? 'reason' : 'people' })}
          >
            <ChevronLeft size={16} aria-hidden /> Back
          </button>
        </div>
      )}

      {state.step === 'q2' && (
        <div className={styles.card}>
          {stepDots}
          <div className={styles.question}>{SUPPORTING_DETAIL_PROMPT}</div>
          <div className={styles.options}>
            {supportingDetailOptions.map((opt) => (
              <OptionBtn
                key={opt.value}
                label={opt.label}
                selected={state.supportingDetails.includes(opt.value)}
                onClick={() => toggleSupportingDetail(opt.value)}
                disabled={
                  !state.supportingDetails.includes(opt.value) &&
                  state.supportingDetails.length >= MAX_SUPPORTING_DETAILS
                }
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => {
              trackStep('detail');
              set({ step: 'ownWords' });
            }}
          >
            Next
          </button>
          <button type="button" className={styles.backBtn} onClick={() => set({ step: 'q1' })}>
            <ChevronLeft size={16} aria-hidden /> Back
          </button>
        </div>
      )}

      {state.step === 'ownWords' && (
        <div className={styles.card}>
          {stepDots}
          <div className={styles.question}>{OWN_WORDS_PROMPT}</div>
          <p className={styles.sub}>{OWN_WORDS_SUBTITLE}</p>
          <textarea
            className={styles.privateArea}
            value={state.optionalDetail}
            onChange={(e) => set({ optionalDetail: e.target.value.slice(0, OWN_WORDS_MAX_LENGTH) })}
            rows={3}
            maxLength={OWN_WORDS_MAX_LENGTH}
            placeholder={OWN_WORDS_PLACEHOLDER}
          />
          <p className={styles.charCounter}>
            {state.optionalDetail.length}/{OWN_WORDS_MAX_LENGTH}
          </p>
          <p className={styles.sub}>{OWN_WORDS_PRIVACY_NOTE}</p>
          <button type="button" className={styles.btnPrimary} onClick={assembleDraft}>
            {state.optionalDetail.trim() ? 'Continue' : 'Skip for now'}
          </button>
          <button type="button" className={styles.backBtn} onClick={() => set({ step: 'q2' })}>
            <ChevronLeft size={16} aria-hidden /> Back
          </button>
        </div>
      )}

      {state.step === 'reason' && hasReasonStep && (
        <div className={styles.card}>
          {stepDots}
          <div className={styles.question}>{REASON_PROMPT}</div>
          <div className={styles.options}>
            {questions.reasons.map((reason) => (
              <OptionBtn
                key={reason.value}
                label={reason.label}
                selected={state.reasonForVisit === reason.value}
                onClick={() => selectReason(reason.value)}
              />
            ))}
          </div>
          <button type="button" className={styles.backBtn} onClick={() => set({ step: 'people' })}>
            <ChevronLeft size={16} aria-hidden /> Back
          </button>
        </div>
      )}

      {state.step === 'people' && (
        <div className={styles.card}>
          {stepDots}
          {/* Bridge after the 5-star tap: a third of raters bailed here in the
              pilot funnel (median 9s), reading the rating as the end of the
              flow. Reframe the questions that follow as a favor to the patient. */}
          <p className={styles.bridge}>
            Wonderful! Let&apos;s turn that into a review. It takes about 30 seconds, and we&apos;ll
            write it for you.
          </p>
          <div className={styles.question}>{PEOPLE_PROMPT}</div>
          {staffPeople.length > 0 && (
            <div className={styles.options}>
              {staffPeople.map((person) => (
                <OptionBtn
                  key={person.name}
                  label={person.name}
                  selected={state.people.includes(person.name)}
                  onClick={() => togglePerson(person.name)}
                  disabled={
                    !state.people.includes(person.name) && selectedPeopleCount >= MAX_PEOPLE
                  }
                />
              ))}
            </div>
          )}
          {/* Free-typed names — another input appears once one is filled, until
              chips + write-ins reach MAX_PEOPLE. Also the only entry path for
              locations with no staff roster yet. */}
          <div className={styles.contactFields}>
            {state.peopleWriteIns.map((value, i) => (
              <input
                key={i}
                type="text"
                className={styles.contactInput}
                value={value}
                onChange={(e) => setWriteIn(i, e.target.value)}
                maxLength={60}
                placeholder={
                  staffPeople.length > 0 ? 'Someone else? Type their name' : 'Type their name'
                }
                aria-label="Add a person by name"
              />
            ))}
          </div>
          <button type="button" className={styles.btnPrimary} onClick={goToAfterPeople}>
            Next
          </button>
          <button type="button" className={styles.backBtn} onClick={onBackToIntro}>
            <ChevronLeft size={16} aria-hidden /> Back
          </button>
        </div>
      )}

      {state.step === 'expectation' && (
        <CaptureExpectationScreen
          onContinue={() => {
            trackStep('expectation');
            set({ step: 'share' });
          }}
          onBack={() => set({ step: 'ownWords' })}
        />
      )}

      {state.step === 'share' && (
        <CaptureShareScreen
          resolution={resolution}
          shortCode={shortCode}
          rating={rating}
          draft={state.draft}
          draftLoading={state.draftLoading}
          onChangeDraft={(v) => set({ draft: v })}
          copyToClipboard
          onBack={() => set({ step: 'ownWords' })}
          patientInputs={patientInputs}
          patientFreeText={state.optionalDetail.trim()}
        />
      )}
    </>
  );
}
